/**
 * v0.9.10 A — measured ThaiWater trend / daily peaks (from /api/tw-station). One formatter for the
 * station card, the lower-river card and the summary. Measured values only: the peak line says
 * "วัดจริง" and never "พยากรณ์", and nothing guesses the next high water.
 */
export interface Trend6 { kind: "up" | "down" | "flat" | "insufficient"; cmPerHour: number | null; n: number; expected: number }
export interface Peaks { today: { t: string; v: number } | null; yesterday: { t: string; v: number } | null; latest?: { t: string; v: number } | null; todayEnough?: boolean }
export interface TwStation { sid: number; available: boolean; trend6?: Trend6; tidal?: { river: string; km: number; dKm: number } | null; peaks?: Peaks | null; stale?: boolean }

const cache = new Map<number, { at: number; p: Promise<TwStation | null> }>();
/** One fetch per station per 5 min on this page (the API caches the ThaiWater series 10 min). */
export function loadTwStation(sid: number): Promise<TwStation | null> {
  const hit = cache.get(sid);
  if (hit && Date.now() - hit.at < 5 * 60e3) return hit.p;
  const p = fetch(`/api/tw-station?sid=${sid}`).then((r) => (r.ok ? (r.json() as Promise<TwStation>) : null)).catch(() => null);
  cache.set(sid, { at: Date.now(), p });
  return p;
}

const rate = (x: number): string => { const a = Math.abs(x); return a >= 10 ? String(Math.round(a)) : a.toFixed(1); };
/** "กำลังขึ้น X ซม./ชม." · "กำลังลง X ซม./ชม." · "ทรงตัว" · "ข้อมูลไม่พอบอกแนวโน้ม" */
export function trendText(t: Trend6 | undefined | null): string {
  if (!t || t.kind === "insufficient" || t.cmPerHour == null) return "ข้อมูลไม่พอบอกแนวโน้ม";
  if (t.kind === "flat") return "ทรงตัว";
  return `${t.kind === "up" ? "กำลังขึ้น" : "กำลังลง"} ${rate(t.cmPerHour)} ซม./ชม.`;
}
const hhmm = (iso: string): string => iso.slice(11, 16);
const TH_MON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const thToday = (): string => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
/** "เมื่อวาน" or "5 ต.ค." — the Thai day of a peak that is not today */
function dayWord(iso: string): string {
  const d = iso.slice(0, 10), y = new Date(Date.parse(`${thToday()}T00:00:00Z`) - 86400e3).toISOString().slice(0, 10);
  return d === y ? "เมื่อวาน" : `${Number(d.slice(8, 10))} ${TH_MON[Number(d.slice(5, 7)) - 1]}`;
}
/** v0.9.10b — high waters only ("ยอดน้ำขึ้น": rose ≥ 30 min before, fell ≥ 30 min after; measured):
 *  "ยอดน้ำขึ้นวันนี้ HH:MM น. (วัดจริง) · เมื่อวาน HH:MM น."
 *  "วันนี้ยังไม่ถึงยอดน้ำขึ้น · ยอดล่าสุด เมื่อวาน HH:MM น. (วัดจริง)"  (a day's 00:00 on a falling level is never a peak) */
export function peaksText(p: Peaks | null | undefined): string | null {
  if (!p) return null;
  if (p.today) return `ยอดน้ำขึ้นวันนี้ ${hhmm(p.today.t)} น. (วัดจริง)${p.yesterday ? ` · เมื่อวาน ${hhmm(p.yesterday.t)} น.` : ""}`;
  const last = p.latest ?? p.yesterday;
  if (!last) return null;
  const head = p.todayEnough === false ? "ข้อมูลวันนี้ยังไม่พอบอกยอดน้ำขึ้น" : "วันนี้ยังไม่ถึงยอดน้ำขึ้น";
  return `${head} · ยอดล่าสุด ${dayWord(last.t)} ${hhmm(last.t)} น. (วัดจริง)`;
}
